import type { GraphicsRequirements } from '@/graphics/GraphicsRequirements'
import type { EngineContext } from '@/runtime/EngineContext'
import { type PhaseRenderContext, RenderPhase } from '@/shared/types/assets'
import type { Plugin } from '@/shared/types/plugin'
import type { RenderContributor, RenderPipeline } from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import { SharedOcclusionDepthServiceImpl } from './SharedOcclusionDepthService'

/** 渲染管线级共享遮挡深度插件。 */
export class SharedOcclusionDepthPlugin implements Plugin {
  /** Plugin registry name. */
  readonly name = 'shared-occlusion-depth'
  readonly graphicsRequirements: GraphicsRequirements = {
    backends: ['webgl2', 'webgpu'],
    capabilities: ['surfaceRender'],
  }
  /** Plugin startup priority. */
  readonly priority = 24
  /** Services required before this plugin starts. */
  readonly dependencies = ['RenderPipeline'] as const
  /** Services registered by this plugin. */
  readonly provides = ['SharedOcclusionDepthService'] as const

  private context: EngineContext | null = null
  private renderPipeline: RenderPipeline | null = null
  private service: SharedOcclusionDepthServiceImpl | null = null
  private readonly renderContributor: RenderContributor = {
    kind: 'shared-occlusion-depth',
    graphicsRequirements: {
      backends: ['webgl2', 'webgpu'],
      capabilities: ['surfaceRender'],
    },
    phases: [RenderPhase.OcclusionDepth],
    renderPriority: 0,
    render: (frame, _phase, renderContext) => {
      this.render(frame, renderContext)
    },
  }

  /**
   * Registers the shared occlusion depth service.
   *
   * @param context - Engine context used for service registration.
   */
  onInit(context: EngineContext): void {
    this.context = context
    this.service = new SharedOcclusionDepthServiceImpl((reason) => {
      const serviceContext = context as {
        hasService?: (name: string) => boolean
        getService?: (name: string) => unknown
      }
      if (!serviceContext.getService) {
        return
      }
      if (serviceContext.hasService && !serviceContext.hasService('RenderInvalidationService')) {
        return
      }
      const service = serviceContext.getService('RenderInvalidationService') as
        | { invalidate?: (reason: string) => void }
        | undefined
      service?.invalidate?.(reason)
    })
    context.registerService('SharedOcclusionDepthService', this.service)
  }

  /** Registers the depth render contributor. */
  onStart(): void {
    if (!this.context || !this.service) {
      return
    }

    this.renderPipeline = this.context.getService('RenderPipeline')
    this.renderPipeline.addRenderContributor(this.renderContributor)
  }

  /** Removes render contribution and disposes the service. */
  onDestroy(): void {
    this.renderPipeline?.removeRenderContributor(this.renderContributor)
    this.service?.dispose()
    this.renderPipeline = null
    this.service = null
    this.context = null
  }

  private render(frame: ViewFrameContext, renderContext: PhaseRenderContext): void {
    this.service?.render(frame, renderContext)
  }
}
